import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { FuelLogByVehicleType } from "../@types";
import { DashCard, EmptyState } from "./DashCard";
import { Fuel } from "lucide-react";

interface FuelConsumptionData {
  fuelLog: FuelLogByVehicleType[];
  className?: string;
}

const COLORS = ["#0E7A99", "#38BDF8", "#86BC25", "#F59E0B", "#94A3B8"];

const FuelConsumptionChart: React.FC<FuelConsumptionData> = ({
  fuelLog,
  className,
}) => {
  const total = fuelLog.reduce((sum, fuel) => sum + fuel.totalFuelConsumed, 0);

  const chartData = fuelLog.map((fuel, idx) => ({
    name: fuel.vehicleType,
    value: fuel.totalFuelConsumed,
    color: COLORS[idx % COLORS.length],
  }));

  return (
    <DashCard
      title="Fuel consumption"
      subtitle="Litres consumed per vehicle type"
      className={className}
    >
      {total === 0 ? (
        <EmptyState icon={<Fuel size={20} />} message="No fuel logged yet" />
      ) : (
        <div className="flex flex-col items-center gap-6 sm:flex-row">
          <div className="relative h-[220px] w-full max-w-[220px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="62%"
                  outerRadius="92%"
                  dataKey="value"
                  stroke="white"
                  strokeWidth={3}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value} Litres`}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "0.75rem",
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-2xl font-semibold text-slate-900">
                {total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-xs text-slate-500">Litres</p>
            </div>
          </div>

          <ul className="w-full space-y-3">
            {chartData.map((fuel) => (
              <li
                key={fuel.name}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: fuel.color }}
                  />
                  <span className="truncate text-sm text-slate-600">
                    {fuel.name}
                  </span>
                </div>
                <span className="shrink-0 text-sm font-semibold text-slate-900">
                  {fuel.value.toLocaleString()} L
                  <span className="ml-1.5 text-xs font-normal text-slate-400">
                    {Math.round((fuel.value / total) * 100)}%
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashCard>
  );
};

export default FuelConsumptionChart;
